import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { firmsApi, type FirmRole, type FirmSubscription } from '../lib/api';

export default function DashboardPage() {
  const { user } = useAuth();

  // Firm role + subscriptions, read from firms-api GET /api/firms/me (Phase 3.7).
  const [firmRole, setFirmRole] = useState<FirmRole | null>(null);
  const [subscriptions, setSubscriptions] = useState<FirmSubscription[]>([]);
  const [firmLoading, setFirmLoading] = useState(true);
  const [firmError, setFirmError] = useState<string | null>(null);

  const loadFirm = useCallback(async () => {
    setFirmLoading(true);
    setFirmError(null);
    try {
      const me = await firmsApi.getMe();
      setFirmRole(me.firm_role);
      setSubscriptions(me.subscriptions);
    } catch (err) {
      setFirmError(err instanceof Error ? err.message : 'Failed to load firm');
    } finally {
      setFirmLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFirm();
  }, [loadFirm]);

  // ProtectedRoute guarantees an authenticated user before this renders; this
  // guard is belt-and-suspenders and also narrows the type below.
  if (!user) {
    return null;
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();

  return (
    <>
        <div>
          <h1 className="text-xl font-semibold text-gray-800">
            Welcome{fullName ? `, ${fullName}` : ''}
          </h1>
          <p className="text-sm text-gray-500">Signed in with your Peeler credentials.</p>
        </div>

        {/* Peeler identity — sourced from /api/auth/me only (Phase 3.6 ruling C) */}
        <section className="bg-white rounded-boby-lg border border-slate-200 p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Your Peeler identity</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Field label="Name" value={fullName || '—'} />
            <Field label="Email" value={user.email} />
            <Field label="TelePathCode" value={user.telepathcode || '—'} />
            <Field label="Roles" value={user.roles.length ? user.roles.join(', ') : '—'} />
            <Field label="Bond status" value={user.bond_status || '—'} />
            <Field label="Country" value={user.country_code || '—'} />
            <Field label="Service tier" value={user.service_tier || '—'} />
          </dl>
        </section>

        {/* Firm role — real data via firms-api GET /api/firms/me (Phase 3.7) */}
        <section className="bg-white rounded-boby-lg border border-slate-200 p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Your firm</h2>

          {firmLoading && <p className="text-sm text-gray-500">Loading your firm…</p>}

          {!firmLoading && firmError && (
            <div className="space-y-2">
              <p role="alert" className="text-sm text-red-600">{firmError}</p>
              <button
                type="button"
                onClick={() => void loadFirm()}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 underline"
              >
                Try again
              </button>
            </div>
          )}

          {!firmLoading && !firmError && !firmRole && (
            <p className="text-sm text-gray-500">
              You do not have a Security Firm role yet. Once your firm is registered, its
              details and postcode subscriptions will appear here.
            </p>
          )}

          {!firmLoading && !firmError && firmRole && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Field label="Firm name" value={firmRole.firm_name || '—'} />
              <Field label="Verification status" value={firmRole.verification_status || '—'} />
              <Field label="Country" value={firmRole.country_code || '—'} />
              <Field label="Founding firm" value={firmRole.is_founding_firm ? 'Yes' : 'No'} />
              <Field label="Business number" value={firmRole.business_number || '—'} />
              <Field label="Licence number" value={firmRole.licence_number || '—'} />
            </dl>
          )}
        </section>

        {/* Postcode subscriptions — only shown to a firm-role holder (Phase 3.7) */}
        {!firmLoading && !firmError && firmRole && (
          <section className="bg-white rounded-boby-lg border border-slate-200 p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Postcode subscriptions</h2>

            {subscriptions.length === 0 ? (
              <p className="text-sm text-gray-500 mb-4">No active postcode subscriptions yet.</p>
            ) : (
              <ul className="divide-y divide-slate-200 mb-4">
                {subscriptions.map((s) => (
                  <li key={s.id} className="py-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-800">{s.postcode}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-gray-400">{s.country_code}</span>
                      {s.is_founding_member && (
                        <span className="text-xs font-medium text-gray-900 bg-boby-gold rounded-boby-sm px-2 py-0.5">
                          Founding member
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <SubscriptionForm onSubscribed={loadFirm} />
          </section>
        )}

        {/* Broker moments — link to the live request feed (Phase 4 Step 7) */}
        <section className="bg-white rounded-boby-lg border border-slate-200 p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Broker moments</h2>
          <p className="text-sm text-gray-500 mb-4">
            View open requests on your subscribed postcodes and acknowledge to take the routing.
          </p>
          <Link
            to="/broker-moments"
            className="inline-block rounded-boby-sm bg-boby-gold px-4 py-2 font-semibold text-gray-900 transition hover:brightness-95"
          >
            Open broker moments
          </Link>
        </section>
    </>
  );
}

function SubscriptionForm({ onSubscribed }: { onSubscribed: () => Promise<void> }) {
  const [postcode, setPostcode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = postcode.trim();
    if (!trimmed) {
      setError('Please enter a postcode.');
      return;
    }
    setSubmitting(true);
    try {
      await firmsApi.subscribePostcode(trimmed);
      setPostcode('');
      await onSubscribed(); // refetch so the new subscription appears
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-slate-200 pt-4 space-y-3" noValidate>
      <label htmlFor="postcode" className="block text-sm font-medium text-gray-700">
        Subscribe to a postcode
      </label>
      <div className="flex items-start gap-2">
        <input
          id="postcode"
          type="text"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          placeholder="e.g. 4000"
          disabled={submitting}
          className="flex-1 rounded-boby-sm border border-gray-300 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-boby-gold disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={submitting}
          className="rounded-boby-sm bg-boby-gold px-4 py-2 font-semibold text-gray-900 transition hover:brightness-95 disabled:opacity-60"
        >
          {submitting ? 'Subscribing…' : 'Subscribe'}
        </button>
      </div>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </form>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-400">{label}</dt>
      <dd className="text-gray-800">{value}</dd>
    </div>
  );
}
